import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link as MuiLink,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  quotationsApi,
  type CreateQuotationRequest
} from '@/api/modules/quotations';
import { customersApi, type CustomerSummary } from '@/api/modules/customers';
import { ApiException } from '@/api/types';
import { paths } from '@/router/paths';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];

interface FormState {
  customerId: string;
  totalAmount: string;
  currency: string;
  validUntil: string;
}

/**
 * Minimal quotation creation form.
 *
 * Post-Wave-1 changes:
 *   - Code field is gone — the server auto-generates {@code QT-YYYY-000001}.
 *   - Customer is picked from the master via Autocomplete; the customer's
 *     code + name are snapshotted server-side. There's a "+ New customer"
 *     escape hatch to the customer create page.
 */
export function QuotationCreatePage() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    customerId: '',
    totalAmount: '',
    currency: 'INR',
    validUntil: defaultValidUntil()
  });

  const customerOptions = useQuery({
    queryKey: ['customers', 'summaries', 0],
    queryFn: () => customersApi.summaries(0, 50)
  });

  const selectedCustomer =
    customerOptions.data?.items.find((c) => c.id === form.customerId) ?? null;

  const mutation = useMutation({
    mutationFn: (req: CreateQuotationRequest) => quotationsApi.create(req),
    onSuccess: (created) => {
      navigate(paths.quotationDetail.replace(':id', created.id), { replace: true });
    }
  });

  const fieldErrors = extractFieldErrors(mutation.error);
  const topLevelError =
    mutation.error && !(mutation.error instanceof ApiException)
      ? (mutation.error as Error).message
      : mutation.error instanceof ApiException && !mutation.error.error.fieldErrors?.length
        ? mutation.error.error.message
        : null;

  const handleChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customerId) return;
    mutation.mutate({
      customerId: form.customerId,
      totalAmount: form.totalAmount,
      currency: form.currency,
      validUntil: form.validUntil
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h1" gutterBottom>
        New quotation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Code is generated automatically (<code>QT-YYYY-000001</code>). Starts
        in <b>DRAFT</b>; send it from the detail page when ready.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={customerOptions.data?.items ?? []}
                loading={customerOptions.isLoading}
                getOptionLabel={(c) => `${c.code} · ${c.name}`}
                value={selectedCustomer}
                onChange={(_, selected: CustomerSummary | null) => {
                  setForm((f) => ({
                    ...f,
                    customerId: selected?.id ?? '',
                    currency: selected?.currency ?? f.currency
                  }));
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Customer"
                    error={Boolean(fieldErrors.customerId)}
                    helperText={fieldErrors.customerId ?? (
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={() => navigate(paths.customerCreate)}
                        underline="hover"
                      >
                        + New customer
                      </MuiLink>
                    )}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerOptions.isLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>

            {selectedCustomer && (
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  Rate for <b>{selectedCustomer.name}</b>: {selectedCustomer.currency}{' '}
                  {selectedCustomer.perWhRate} / Wh (used when pricing EV battery
                  quotations in a later step).
                </Alert>
              </Grid>
            )}

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                label="Total amount"
                helperText={fieldErrors.totalAmount ?? 'e.g. 12500.00'}
                error={Boolean(fieldErrors.totalAmount)}
                value={form.totalAmount}
                onChange={handleChange('totalAmount')}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                select
                label="Currency"
                error={Boolean(fieldErrors.currency)}
                helperText={fieldErrors.currency ?? undefined}
                value={form.currency}
                onChange={handleChange('currency')}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Valid until"
                InputLabelProps={{ shrink: true }}
                helperText={fieldErrors.validUntil ?? 'Must be a future date'}
                error={Boolean(fieldErrors.validUntil)}
                value={form.validUntil}
                onChange={handleChange('validUntil')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {topLevelError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {topLevelError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          type="button"
          variant="text"
          onClick={() => navigate(paths.quotations)}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={mutation.isPending || !form.customerId}
        >
          {mutation.isPending ? 'Creating…' : 'Create quotation'}
        </Button>
      </Stack>
    </Box>
  );
}

function defaultValidUntil(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function extractFieldErrors(err: unknown): Record<string, string | undefined> {
  if (!(err instanceof ApiException)) return {};
  const fields = err.error.fieldErrors ?? [];
  const out: Record<string, string> = {};
  for (const fe of fields) out[fe.field] = fe.message;
  return out;
}
